import { useRef, useState, useMemo, useEffect, type DragEvent } from 'react';
import { FileVideo, ImageIcon, Check, Loader2, GripVertical, Play, Music, Volume2, ZoomIn, X } from 'lucide-react';

/**
 * 素材确认卡片数据结构（与服务端 metadata.videoMaterialConfirm 一致）
 */
export interface VideoMaterialVideo { index: number; fileName: string; /** 用户上传时的原始文件名（展示用） */ displayName?: string; /** ffprobe 探测时长（秒，方案A首回前置探测） */ duration?: number }
export interface VideoMaterialImage {
  fileName: string; url: string; role: string;
  /** filename=文件名已识别出槽位（免下拉）；unknown=未识别（展示下拉） */
  source?: 'filename' | 'unknown';
}
export interface VideoMaterialEndCard { fileName: string }
export interface VideoMaterialAudio { fileName: string; url?: string; slot?: 'sfx' | 'bgm' }
export interface VideoMaterialConfirmData {
  videos: VideoMaterialVideo[];
  images: VideoMaterialImage[];
  endCards?: VideoMaterialEndCard[];
  audios?: VideoMaterialAudio[];
}

interface VideoMaterialConfirmCardProps {
  material: VideoMaterialConfirmData;
  onConfirm?: (videoOrder: string[], imageRoles: Array<{ fileName: string; role: string }>, videoOrderIndexes?: number[]) => void;
  loading?: boolean;
  confirmed?: boolean;
}

/** 图片槽位下拉选项（与后端 UploadedImageRole 枚举对齐，文案复用 UPLOADED_IMAGE_ROLE_LABEL） */
const ROLE_OPTIONS: Array<{ value: string; label: string }> = [
  { value: 'cta_button', label: 'CTA 按钮图（将作为结束页下载按钮）' },
  { value: 'guide_hand', label: '手势引导图（将作为首段手势提示）' },
  { value: 'endcard_bg', label: '结束页背景图' },
  { value: 'icon', label: '结束页图标' },
  { value: 'other', label: '普通素材' },
];

/** 音频槽位标签 */
const AUDIO_SLOT_LABEL: Record<string, string> = { sfx: '音效', bgm: '背景音乐' };

/**
 * 视频转试玩素材确认卡片（阶段一：确认视频顺序与图片/音频槽位）
 *
 * 展示收到的视频播放顺序（HTML5 原生拖拽排序 + 过程动画反馈）与图片槽位（下拉修改），
 * 用户确认后回传 videoOrder（fileName 序列）与 imageRoles，服务端据此生成方案卡。
 * 注意：此卡片不再展示 AI 方案（已拆到阶段二的方案确认卡）。
 */
export function VideoMaterialConfirmCard({
  material,
  onConfirm,
  loading = false,
  confirmed = false,
}: VideoMaterialConfirmCardProps): JSX.Element {
  const [videoOrder, setVideoOrder] = useState<string[]>(() => material.videos.map((v) => v.fileName));
  const [roleMap, setRoleMap] = useState<Record<string, string>>(() =>
    Object.fromEntries(material.images.map((img) => [img.fileName, img.role])),
  );
  // 拖拽过程态
  const [dragIdx, setDragIdx] = useState<number | null>(null);
  const [hoverIdx, setHoverIdx] = useState<number | null>(null);
  // 图片放大预览
  const [previewImg, setPreviewImg] = useState<{ url: string; fileName: string } | null>(null);
  const [playingAudio, setPlayingAudio] = useState<string | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const locked = confirmed || loading;

  /** fileName → 用户上传时的原始文件名（展示用，uuid 名用户无法辨认） */
  const displayNameOf = (fileName: string) =>
    material.videos.find((v) => v.fileName === fileName)?.displayName || fileName;

  const imageCount = material.images.length + (material.endCards?.length ?? 0);

  /**
   * 拖拽期的展示顺序：把 dragIdx 元素预览性地移到 hoverIdx，不落真实 state。
   * 松手后才 commit 到 videoOrder。
   */
  const displayOrder = useMemo(() => {
    if (dragIdx === null || hoverIdx === null || dragIdx === hoverIdx) return videoOrder;
    const arr = [...videoOrder];
    const [moved] = arr.splice(dragIdx, 1);
    arr.splice(hoverIdx, 0, moved);
    return arr;
  }, [videoOrder, dragIdx, hoverIdx]);

  // ── 拖拽事件处理（7.2-2） ──
  const handleDragStart = (e: DragEvent<HTMLDivElement>, i: number) => {
    if (locked) return;
    setDragIdx(i);
    setHoverIdx(i);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', String(i));
    // 以行元素本身作拖拽幽灵
    const node = e.currentTarget;
    e.dataTransfer.setDragImage(node, 24, node.offsetHeight / 2);
  };

  const handleDragOver = (e: DragEvent<HTMLDivElement>, i: number) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (locked || dragIdx === null) return;
    if (hoverIdx !== i) setHoverIdx(i);
  };

  const commitOrder = () => {
    if (dragIdx !== null && hoverIdx !== null && dragIdx !== hoverIdx) setVideoOrder(displayOrder);
    setDragIdx(null);
    setHoverIdx(null);
  };

  const handleDragEnd = () => commitOrder();

  const handleDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    commitOrder();
  };

  // ── 图片放大预览（8.2-4 Esc / 滚动锁定） ──
  useEffect(() => {
    if (!previewImg) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setPreviewImg(null); };
    window.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [previewImg]);

  /** 音频试听 */
  const toggleAudio = (fileName: string, url?: string) => {
    if (!url) return;
    if (playingAudio === fileName) {
      audioRef.current?.pause();
      audioRef.current = null;
      setPlayingAudio(null);
      return;
    }
    try {
      audioRef.current?.pause();
      const audio = new Audio(url);
      audioRef.current = audio;
      audio.onended = () => setPlayingAudio(null);
      void audio.play().catch(() => setPlayingAudio(null));
      setPlayingAudio(fileName);
    } catch {
      setPlayingAudio(null);
    }
  };

  const handleConfirm = () => {
    const imageRoles = material.images.map((img) => ({
      fileName: img.fileName,
      role: roleMap[img.fileName] ?? img.role,
    }));
    // 传 index 顺序（以 material.videos[i].index 为准），服务端据此重排即消除 fileName 匹配问题
    const videoOrderIndexes = videoOrder
      .map((fn) => material.videos.find((v) => v.fileName === fn)?.index)
      .filter((n): n is number => typeof n === 'number');
    onConfirm?.(videoOrder, imageRoles, videoOrderIndexes);
  };

  return (
    <div className={`glass-card gradient-border max-w-[90%] overflow-hidden ${locked ? 'opacity-70' : ''}`}>
      {/* 顶部标题条 */}
      <div className="flex items-center gap-2 border-b border-border/40 bg-gradient-to-r from-neon-cyan/[0.08] via-transparent to-neon-purple/[0.08] px-4 py-2.5">
        <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-md border border-neon-cyan/30 bg-neon-cyan/10 shadow-[0_0_12px_hsl(var(--neon-cyan)/0.25)]">
          <FileVideo size={12} className="text-neon-cyan" />
        </span>
        <span className="text-xs font-bold uppercase tracking-[0.18em] text-neon-cyan">
          视频转试玩确认
        </span>
        <span className="ml-auto flex items-center gap-1 rounded-full border border-neon-cyan/30 bg-neon-cyan/10 px-2 py-0.5 text-[10px] font-medium text-neon-cyan">
          <FileVideo size={9} />
          {material.videos.length} 个视频
        </span>
        <span className="flex items-center gap-1 rounded-full border border-neon-purple/30 bg-neon-purple/10 px-2 py-0.5 text-[10px] font-medium text-neon-purple">
          <ImageIcon size={9} />
          {imageCount} 张图片
        </span>
      </div>

      {/* 视频播放顺序（拖拽排序，带过程动画反馈） */}
      <div className="px-4 pt-3 pb-2">
        <div className="mb-1.5 flex items-center gap-1.5">
          <FileVideo size={11} className="text-neon-cyan/60" />
          <span className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground">
            视频播放顺序{!locked && material.videos.length > 1 ? '（拖拽调整）' : ''}
          </span>
        </div>
        <div
          className="overflow-hidden rounded-lg border border-border/40"
          onDragOver={(e) => e.preventDefault()}
          onDrop={handleDrop}
        >
          {displayOrder.map((fileName, i) => {
            const v = material.videos.find((x) => x.fileName === fileName);
            // displayOrder 已把被拖元素移到 hover 位，被拖元素当前所在索引 = hoverIdx
            const isDragging = dragIdx !== null && hoverIdx === i;
            return (
              <div
                key={fileName}
                draggable={!locked}
                onDragStart={(e) => handleDragStart(e, i)}
                onDragOver={(e) => handleDragOver(e, i)}
                onDragEnd={handleDragEnd}
                onDrop={(e) => e.stopPropagation()}
                className={`flex items-center gap-2 px-2.5 py-1.5 text-[11px] transition-all duration-150 ${
                  i > 0 ? 'border-t border-border/20' : ''
                } ${
                  isDragging
                    ? 'scale-[0.98] border-primary/70 bg-primary/5 shadow-lg shadow-primary/20'
                    : 'bg-muted/20'
                } ${locked ? '' : 'cursor-grab active:cursor-grabbing'}`}
                style={!locked && isDragging ? { opacity: 0.5, borderLeft: '2px solid var(--primary)' } : undefined}
              >
                {!locked && <GripVertical size={11} className="shrink-0 text-muted-foreground/50 group-hover:text-muted-foreground" />}
                <span className="w-5 shrink-0 text-right font-bold tabular-nums text-neon-cyan">{i + 1}.</span>
                <span className="truncate text-foreground/90" title={fileName}>{displayNameOf(fileName)}</span>
                {typeof v?.duration === 'number' && (
                  <span className="ml-auto shrink-0 text-[10px] text-muted-foreground">{v.duration.toFixed(1)}s</span>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* 图片槽位（下拉修改 + 缩略图点击放大预览） */}
      {imageCount > 0 && (
        <div className="px-4 pb-2">
          <div className="mb-1.5 flex items-center gap-1.5">
            <ImageIcon size={11} className="text-neon-purple/60" />
            <span className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground">图片槽位</span>
          </div>
          <div className="space-y-1.5">
            {material.images.map((img) => (
              <div key={img.fileName} className="flex items-center gap-2 rounded-lg border border-border/30 bg-muted/20 px-2.5 py-1.5">
                <button
                  type="button"
                  onClick={() => setPreviewImg({ url: img.url, fileName: img.fileName })}
                  title="点击放大预览"
                  className="group relative h-8 w-8 shrink-0 overflow-hidden rounded-md border border-border/40 transition hover:border-primary/60 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/50"
                >
                  <img src={img.url} alt={img.fileName} className="h-full w-full object-cover" />
                  <span className="absolute inset-0 hidden items-center justify-center bg-black/40 group-hover:flex">
                    <ZoomIn size={12} className="text-white" />
                  </span>
                </button>
                <span className="min-w-0 flex-1 truncate text-[11px] text-foreground/85" title={img.fileName}>{img.fileName}</span>
                <select
                  value={roleMap[img.fileName] ?? img.role}
                  disabled={locked}
                  onChange={(e) => setRoleMap((prev) => ({ ...prev, [img.fileName]: e.target.value }))}
                  className="shrink-0 rounded-md border border-border/50 bg-background/80 px-1.5 py-1 text-[10px] text-foreground/90 outline-none disabled:opacity-60"
                >
                  {ROLE_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>
            ))}
            {(material.endCards ?? []).map((ec) => (
              <div key={ec.fileName} className="flex items-center gap-2 rounded-lg border border-border/30 bg-muted/20 px-2.5 py-1.5">
                <span className="min-w-0 flex-1 truncate text-[11px] text-foreground/85" title={ec.fileName}>{ec.fileName}</span>
                <span className="shrink-0 rounded-md border border-emerald-500/30 bg-emerald-500/10 px-1.5 py-1 text-[10px] text-emerald-400">
                  结束页背景（显式上传）
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* 音频/背景音乐槽位 */}
      {(material.audios?.length ?? 0) > 0 && (
        <div className="px-4 pb-2">
          <div className="mb-1.5 flex items-center gap-1.5">
            <Music size={11} className="text-neon-blue/60" />
            <span className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground">音频 / 背景音乐</span>
          </div>
          <div className="space-y-1.5">
            {(material.audios ?? []).map((audio) => (
              <div key={audio.fileName} className="flex items-center gap-2 rounded-lg border border-border/30 bg-muted/20 px-2.5 py-1.5">
                <button
                  type="button"
                  onClick={() => toggleAudio(audio.fileName, audio.url)}
                  disabled={!audio.url}
                  className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full border border-neon-blue/30 bg-neon-blue/10 text-neon-blue transition-colors hover:bg-neon-blue/20 disabled:opacity-50"
                  title={audio.url ? '试听' : '暂无可播放地址'}
                >
                  {playingAudio === audio.fileName ? <Volume2 size={11} /> : <Play size={11} />}
                </button>
                <span className="min-w-0 flex-1 truncate text-[11px] text-foreground/85" title={audio.fileName}>{audio.fileName}</span>
                <span className="shrink-0 rounded-md border border-neon-blue/30 bg-neon-blue/10 px-1.5 py-1 text-[10px] text-neon-blue">
                  {AUDIO_SLOT_LABEL[audio.slot ?? ''] ?? '音频'}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* 底部操作按钮：阶段一只剩素材确认，方案拆到阶段二 */}
      <div className="flex flex-col border-t border-border/40 px-4 py-2.5">
        {confirmed ? (
          <div className="flex items-center justify-end gap-1.5 text-xs font-medium text-neon-green">
            <Check size={13} />
            已确认素材，生成方案中…
          </div>
        ) : (
          <>
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={handleConfirm}
                disabled={locked}
                className="glow-btn flex items-center gap-1.5 rounded-lg px-3.5 py-1.5 text-xs font-semibold text-white transition-all disabled:pointer-events-none disabled:opacity-50"
              >
                {loading ? <Loader2 size={12} className="animate-spin" /> : <Check size={12} />}
                {loading ? '正在生成…' : '确认素材，生成方案'}
              </button>
            </div>
            <p className="mt-1 text-[10px] text-center text-muted-foreground/70">
              确认后我将基于你的素材槽位生成完整互动方案，再由你审阅
            </p>
          </>
        )}
      </div>

      {/* 图片放大预览 lightbox */}
      {previewImg && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label={`图片预览：${previewImg.fileName}`}
          onClick={() => setPreviewImg(null)}
          className="fixed inset-0 z-[999] flex flex-col items-center justify-center gap-3 bg-black/80 p-6 backdrop-blur-sm"
        >
          <div className="flex w-full max-w-[90vw] items-center justify-between gap-3">
            <span className="truncate text-xs text-white/80">{previewImg.fileName}</span>
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); setPreviewImg(null); }}
              aria-label="关闭预览"
              className="rounded-full bg-white/10 p-1.5 text-white transition hover:bg-white/20"
            >
              <X size={16} />
            </button>
          </div>
          <img
            src={previewImg.url}
            alt={previewImg.fileName}
            onClick={(e) => e.stopPropagation()}
            className="max-h-[80vh] max-w-[90vw] rounded-lg object-contain shadow-2xl"
          />
          <span className="text-[10px] text-white/50">点击空白处或按 Esc 关闭</span>
        </div>
      )}
    </div>
  );
}